import PropTypes from 'prop-types';
import { useEffect, useState, useRef, useMemo, useContext } from 'react';
import { useSelector } from 'react-redux';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from "@mui/icons-material/Add";
import { useSearchParams } from 'react-router-dom';
import { TextField, IconButton, Tooltip, InputAdornment } from '@mui/material';
import { getCartInfoItemQty } from './ordersheetUtils';
import { ShopCartContext } from './_ShopViewContext';

QuantityField.propTypes = {
  row: PropTypes.object,
  callback: PropTypes.func,
};

export default function QuantityField({ row, callback = null }) {
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get('categoryId');
  const [quantity, setQuantity] = useState();
  const [isQuantityTextOverflowing, setIsQuantityTextOverflowing] = useState(false);
  const inputRef = useRef(null);
  const { data: {cartInfo} } = useSelector((state) => state.orderSheet);
  const { cart, setCart } = useContext(ShopCartContext);

  useEffect(() => {
    const input = inputRef.current;
    if (input) {
      setIsQuantityTextOverflowing(input.scrollWidth > input.clientWidth);
    }
  }, [quantity]);

  const updateQuantity = (newQuantity) => {
    setQuantity(newQuantity);
    setCart(cart => ({ ...cart, [row.id]: newQuantity }));
    if (callback) callback(newQuantity);
  };

  const cartInfoItemQty = getCartInfoItemQty(row, categoryId, cartInfo);

  const isItemInCart = useMemo(() => cartInfoItemQty > 0, [cartInfoItemQty]);

  useEffect(() => {
    setQuantity(cart[row.id] ?? row.quantity ?? 0);
  }, [cart]);

  const quantityTextField = <TextField
    inputRef={inputRef}
    size="small"
    value={quantity || 0}
    autoComplete="off"
    onChange={(e) => {
      const newValue = Number(e.target.value);
      if (!Number.isNaN(newValue) && newValue >= 0) {
        updateQuantity(newValue);
      }
    }}
    inputProps={{
      style: {
        textAlign: 'center',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        maxWidth: 60,
      }
    }}
    InputProps={{
      style: {
        height: '32px',
        borderRadius: '10px',
        padding: '4px',
      },
      startAdornment: (
        <InputAdornment position="start" sx={{ m: 0 }}>
          <IconButton
            size="small"
            sx={{ p: '1px', height: 22, minWidth: 22 }}
            disabled={quantity <= 0}
            onClick={() => {
              if (quantity > 0) {
                const newValue = quantity - 1;
                updateQuantity(newValue);
              }
            }}
            edge="start"
          >
            <RemoveIcon fontSize="inherit" sx={{ fontSize: 14 }} />
          </IconButton>
        </InputAdornment>
      ),
      endAdornment: (
        <InputAdornment position="end" sx={{ m: 0 }}>
          <IconButton
            size="small"
            sx={{ p: '1px', height: 22, minWidth: 22 }}
            onClick={() => {
              const newValue = (quantity || 0) + 1;
              updateQuantity(newValue);
            }}
            edge="end"
          >
            <AddIcon fontSize="inherit" sx={{ fontSize: 14 }} />
          </IconButton>
        </InputAdornment>
      ),
    }}
  />

return (
  <Tooltip
    title={isQuantityTextOverflowing ? quantity : `${cartInfoItemQty} of item in Cart`}
    placement="top"
    arrow
    disableHoverListener={!(isQuantityTextOverflowing || isItemInCart)}
    disableFocusListener={!(isQuantityTextOverflowing || isItemInCart)}
    disableTouchListener={!(isQuantityTextOverflowing || isItemInCart)}
  >
    {quantityTextField}
  </Tooltip>
);

}